import dayjs from 'dayjs';
import { i18next } from '@frontend/i18n';
import { FormFieldActionTypes } from '@frontend/design-system';

type DateTimeField = {
  value: unknown;
  'aria-invalid'?: boolean;
  error?: string;
};

type DateRangeState = {
  isRecurring: { value: boolean | string | string[] };
  startDate: DateTimeField;
  endDate: DateTimeField;
  startTime: DateTimeField;
  endTime: DateTimeField;
};

type DateRangeAction = {
  type: FormFieldActionTypes;
  payload?: {
    name?: string;
  };
};

const DEFAULT_TIME_REFERENCE_DATE = '2000-01-01';

/**
 * Determines whether the end of an availability rule is invalid relative to its start.
 *
 * - Recurring rules occur within a single day, so the end time must be after the start time.
 * - Non-recurring rules span a full date + time range and may cross into later days, so the
 *   combined end datetime (end date + end time) must be after the combined start datetime
 *   (start date + start time).
 */
export const getIsInvalidRecurringTimeRange = ({
  startDate,
  endDate,
  startTime,
  endTime,
  isRecurring,
}: {
  startDate?: string;
  endDate?: string;
  startTime?: string;
  endTime?: string;
  isRecurring: boolean;
}): boolean => {
  if (!startTime || !endTime) {
    return false;
  }

  if (isRecurring) {
    const start = dayjs(`${DEFAULT_TIME_REFERENCE_DATE} ${startTime}`);
    const end = dayjs(`${DEFAULT_TIME_REFERENCE_DATE} ${endTime}`);

    if (!start.isValid() || !end.isValid()) {
      return false;
    }

    return !end.isAfter(start);
  }

  // Non-recurring rules can span multiple days; fall back to the start date when no end date is set.
  const effectiveEndDate = endDate || startDate;

  if (!startDate || !effectiveEndDate) {
    return false;
  }

  const start = dayjs(`${startDate} ${startTime}`);
  const end = dayjs(`${effectiveEndDate} ${endTime}`);

  if (!start.isValid() || !end.isValid()) {
    return false;
  }

  return !end.isAfter(start);
};

export const applyDateTimeRangeValidation = <T extends DateRangeState>(
  state: T,
  action: DateRangeAction,
  invalidRangeErrorMessage?: string
): T => {
  const startDateValue = typeof state.startDate.value === 'string' ? state.startDate.value : '';
  const endDateValue = typeof state.endDate.value === 'string' ? state.endDate.value : '';
  const startTimeValue = typeof state.startTime.value === 'string' ? state.startTime.value : '';
  const endTimeValue = typeof state.endTime.value === 'string' ? state.endTime.value : '';
  const startDate = dayjs(startDateValue);
  const endDate = dayjs(endDateValue);
  const startTime = startTimeValue ? dayjs(`${DEFAULT_TIME_REFERENCE_DATE} ${startTimeValue}`) : null;
  const endTime = endTimeValue ? dayjs(`${DEFAULT_TIME_REFERENCE_DATE} ${endTimeValue}`) : null;
  const isValidStartAndEndDate = startDate.isValid() && endDate.isValid();
  const isValidStartAndEndTime = startTime?.isValid() && endTime?.isValid();
  const isInvalidDateRange = startDate.isAfter(endDate, 'day');
  const hasInvalidRecurringTimeRange = getIsInvalidRecurringTimeRange({
    startDate: startDateValue,
    endDate: endDateValue,
    startTime: startTimeValue,
    endTime: endTimeValue,
    isRecurring: !!state.isRecurring.value,
  });

  let newState = { ...state };

  if (action.payload?.name === 'endDate' && isValidStartAndEndDate) {
    newState = {
      ...newState,
      endDate: {
        ...newState.endDate,
        'aria-invalid': isInvalidDateRange,
        error: isInvalidDateRange
          ? invalidRangeErrorMessage || i18next.t('End date must be after the start date', { ns: 'schedule' })
          : '',
      },
    };
  }

  if (action.type === FormFieldActionTypes.Blur && action.payload?.name === 'startDate' && isValidStartAndEndDate) {
    newState = {
      ...newState,
      endDate: {
        ...newState.endDate,
        value: isInvalidDateRange ? startDateValue : newState.endDate.value,
        'aria-invalid': false,
        error: '',
      },
    };
  }

  const shouldValidateTimeOnAction =
    action.type === FormFieldActionTypes.Validate ||
    action.payload?.name === 'isRecurring' ||
    action.payload?.name === 'startDate' ||
    action.payload?.name === 'endDate' ||
    action.payload?.name === 'endTime' ||
    action.payload?.name === 'startTime';

  if (shouldValidateTimeOnAction && isValidStartAndEndTime) {
    newState = {
      ...newState,
      endTime: {
        ...newState.endTime,
        'aria-invalid': hasInvalidRecurringTimeRange,
        error: hasInvalidRecurringTimeRange
          ? i18next.t('End time must be after the start time', { ns: 'schedule' })
          : '',
      },
    };
  }

  return newState;
};
